/** @format */

import { PopupAnchor, PopupPosition } from '@/ducks/map/hooks/useMap';
import React, { useMemo } from 'react';
import type { TBuildingRoomsMarker, TRoomMarker } from '../../../api/apiListingMapMarkers';
import BuildingPopup from './BuildingPopup';
import SingleRoomPopup from './SingleRoomPopup';

interface MapPopupProps {
  popupInfo: (TRoomMarker | TBuildingRoomsMarker) & { id: string };
  getPopupAnchor: (latitude: number, longitude: number) => PopupAnchor;
  setPopupInfo: React.Dispatch<React.SetStateAction<((TRoomMarker | TBuildingRoomsMarker) & { id: string }) | null>>;
}

const MapPopup: React.FC<MapPopupProps> = ({ getPopupAnchor, popupInfo, setPopupInfo }) => {
  const { position, verticalOffset } = useMemo(
    () => getPopupAnchor(popupInfo.position.latitude, popupInfo.position.longitude),
    [getPopupAnchor, popupInfo.position.latitude, popupInfo.position.longitude],
  );

  const isBrowser = typeof window !== 'undefined';
  const deviceWidth = isBrowser && window.innerWidth;

  const offset: [number, number] = useMemo(() => {
    const yOffset: number = verticalOffset ?? 25;
    const offsets: Record<PopupPosition, [number, number]> = {
      center: [0, yOffset],
      left: [50, yOffset],
      right: [-50, yOffset],
      top: [0, Number(deviceWidth) < 481 ? 5 : 25],
      bottom: [0, -yOffset],
      'top-left': [25, 0],
      'top-right': [25, 0],
      'bottom-left': [50, 0],
      'bottom-right': [-50, 0],
    };

    return offsets[position] || [0, 25];
  }, [position, verticalOffset]);

  if ('rooms' in popupInfo) {
    // Is a building
    return (
      <BuildingPopup
        rooms={popupInfo.rooms}
        position={position}
        coords={popupInfo.position}
        setPopupInfo={setPopupInfo}
        offset={offset}
      />
    );
  }

  return (
    <SingleRoomPopup
      code={popupInfo.code}
      position={position}
      coords={popupInfo.position}
      setPopupInfo={setPopupInfo}
      offset={offset}
    />
  );
};

export default MapPopup;
